<template>
  <DashboardWidgetCard
    title="Meetings"
    subtitle="Booked in the selected period"
    icon="mdi:calendar-month-outline"
    accent="#0ea5e9"
    :link-to="{ name: MEETINGS_ROUTE }"
    :is-loading="isLoadingStats"
  >
    <div class="meeting-stats">
      <div v-for="stat in statTiles" :key="stat.key" class="meeting-stat">
        <span class="meeting-stat-dot" :style="{ background: stat.color }" />
        <div class="meeting-stat-value">{{ stat.count }}</div>
        <div class="meeting-stat-label">{{ stat.label }}</div>
      </div>
    </div>

    <div class="show-rate">
      <div class="show-rate-header">
        <span>Show rate</span>
        <strong>{{ showRate === null ? "—" : `${showRate.toFixed(0)}%` }}</strong>
      </div>
      <div class="show-rate-track">
        <span class="show-rate-held" :style="{ width: `${heldShare}%` }" />
        <span class="show-rate-noshow" :style="{ width: `${noShowShare}%` }" />
      </div>
    </div>

    <div class="upcoming">
      <div class="upcoming-heading">Up next</div>

      <div v-if="isLoadingUpcoming" class="upcoming-loading">
        <LoadingBars />
      </div>

      <div v-else-if="upcomingMeetings.length === 0" class="upcoming-empty">
        <Icon icon="mdi:calendar-blank-outline" width="28" height="28" />
        <span>No upcoming meetings</span>
      </div>

      <ul v-else class="upcoming-list">
        <li
          v-for="meeting in upcomingMeetings"
          :key="meeting.id"
          class="upcoming-item"
          role="button"
          tabindex="0"
          :aria-label="`View meeting with ${contactName(meeting)}`"
          @click="openMeetingDetails(meeting)"
          @keydown.enter.prevent="openMeetingDetails(meeting)"
          @keydown.space.prevent="openMeetingDetails(meeting)"
        >
          <div class="upcoming-date" :class="{ 'upcoming-date--today': isToday(meeting) }">
            <span class="upcoming-date-month">{{ formatMonth(meeting.meetingDateTime) }}</span>
            <span class="upcoming-date-day">{{ formatDay(meeting.meetingDateTime) }}</span>
          </div>
          <div class="upcoming-info">
            <div class="upcoming-name">{{ contactName(meeting) }}</div>
            <div class="upcoming-meta">
              <Icon icon="mdi:clock-outline" width="12" height="12" />
              {{ formatTime(meeting.meetingDateTime) }}
              <template v-if="meeting.contact?.company?.name">
                <span class="upcoming-sep">·</span>
                <span class="upcoming-company">{{ meeting.contact.company.name }}</span>
              </template>
            </div>
          </div>
          <span v-if="isToday(meeting)" class="upcoming-badge">Today</span>
          <a
            v-else-if="isSafeUrl(meeting.meetingURL)"
            :href="meeting.meetingURL"
            target="_blank"
            rel="noopener noreferrer"
            class="upcoming-join"
            title="Open meeting link"
            @click.stop
            @keydown.stop
          >
            <Icon icon="mdi:video-outline" width="16" height="16" />
          </a>
        </li>
      </ul>
    </div>

    <MeetingDetailsModal
      v-if="showMeetingDetailsModal"
      :selectedMeeting="selectedMeeting"
      @close-meeting-details-modal="closeMeetingDetails"
      @refresh-meetings="refreshMeetings"
    />
  </DashboardWidgetCard>
</template>

<script setup>
import { computed, ref } from "vue";
import { Icon } from "@iconify/vue";
import { useQueryClient } from "@tanstack/vue-query";
import DashboardWidgetCard from "./DashboardWidgetCard.vue";
import LoadingBars from "@/components/ui/LoadingBars.vue";
import MeetingDetailsModal from "@/components/meeting/MeetingDetailsModal.vue";
import { useMeetings, useMeetingStats } from "@/composables/meetings";
import { MEETINGS_ROUTE } from "@/router/routes";

defineOptions({
  name: "DashboardMeetingsWidget",
});

const props = defineProps({
  organizationId: { type: String, default: null },
  startDate: { type: String, default: null },
  endDate: { type: String, default: null },
  /** Contact owner to scope meetings to. `undefined` means all owners. */
  contactOwnerId: { type: String, default: undefined },
});

const UPCOMING_LIMIT = 5;
const offset = new Date().getTimezoneOffset();

const ownerScope = computed(() =>
  props.contactOwnerId !== undefined ? { contactOwnerId: props.contactOwnerId } : {}
);

const statsParams = computed(() => {
  if (!props.organizationId) return null;
  return {
    organizationId: props.organizationId,
    offset,
    ...(props.startDate && { startDate: props.startDate }),
    ...(props.endDate && { endDate: props.endDate }),
    ...ownerScope.value,
  };
});

const upcomingParams = (schedule) =>
  computed(() => {
    if (!props.organizationId) return null;
    return {
      organizationId: props.organizationId,
      page: 1,
      limit: UPCOMING_LIMIT,
      schedule,
      offset,
      ...ownerScope.value,
    };
  });

const { data: stats, isLoading: isLoadingStats } = useMeetingStats(statsParams);
const { data: dueData, isLoading: isLoadingDue } = useMeetings(upcomingParams("due"));
const { data: futureData, isLoading: isLoadingFuture } = useMeetings(upcomingParams("future"));

const isLoadingUpcoming = computed(() => isLoadingDue.value || isLoadingFuture.value);

const statTiles = computed(() => [
  { key: "pending", label: "Booked", count: stats.value?.pendingCount || 0, color: "#f59e0b" },
  { key: "held", label: "Held", count: stats.value?.meetingHeldCount || 0, color: "#10b981" },
  { key: "no-show", label: "No show", count: stats.value?.noShowCount || 0, color: "#ef4444" },
]);

const heldCount = computed(() => stats.value?.meetingHeldCount || 0);
const noShowCount = computed(() => stats.value?.noShowCount || 0);
const resolvedCount = computed(() => heldCount.value + noShowCount.value);

const showRate = computed(() =>
  resolvedCount.value > 0 ? (heldCount.value / resolvedCount.value) * 100 : null
);
const heldShare = computed(() =>
  resolvedCount.value > 0 ? (heldCount.value / resolvedCount.value) * 100 : 0
);
const noShowShare = computed(() =>
  resolvedCount.value > 0 ? (noShowCount.value / resolvedCount.value) * 100 : 0
);

// Today's unresolved meetings first, then future ones, soonest first.
const upcomingMeetings = computed(() => {
  const rows = [...(dueData.value?.data || []), ...(futureData.value?.data || [])];
  const seen = new Set();
  return rows
    .filter((m) => m?.meetingDateTime && !seen.has(m.id) && seen.add(m.id))
    .sort((a, b) => new Date(a.meetingDateTime) - new Date(b.meetingDateTime))
    .slice(0, UPCOMING_LIMIT);
});

// Meeting details, opened from an "Up next" row (same modal as the Meetings page).
const queryClient = useQueryClient();
const showMeetingDetailsModal = ref(false);
const selectedMeeting = ref(null);

const openMeetingDetails = (meeting) => {
  selectedMeeting.value = meeting;
  showMeetingDetailsModal.value = true;
};

const closeMeetingDetails = () => {
  showMeetingDetailsModal.value = false;
  selectedMeeting.value = null;
};

// After an edit or delete in the modal, reload the list and the counts.
const refreshMeetings = () => {
  queryClient.invalidateQueries({ queryKey: ["meetings"] });
  queryClient.invalidateQueries({ queryKey: ["meetingStats"] });
};

// Only web links: a `javascript:` URL in href would run on click.
const isSafeUrl = (url) => typeof url === "string" && /^https?:\/\//i.test(url.trim());

const isToday = (meeting) =>
  new Date(meeting.meetingDateTime).toDateString() === new Date().toDateString();

const contactName = (meeting) => {
  const name = [meeting.contact?.fname, meeting.contact?.lname].filter(Boolean).join(" ");
  return name || meeting.contact?.email || "Unknown contact";
};

const formatMonth = (value) => new Date(value).toLocaleDateString(undefined, { month: "short" });
const formatDay = (value) => new Date(value).getDate();
const formatTime = (value) =>
  new Date(value).toLocaleString(undefined, {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });
</script>

<style scoped>
.meeting-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.meeting-stat {
  position: relative;
  padding: 12px 14px;
  border-radius: 12px;
  background: #fafafc;
  border: 1px solid #f0f0f5;
}

.meeting-stat-dot {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.meeting-stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
}

.meeting-stat-label {
  margin-top: 2px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #6b7280;
}

.show-rate {
  margin-top: 14px;
}

.show-rate-header {
  display: flex;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #6b7280;
  margin-bottom: 6px;
}

.show-rate-header strong {
  color: #111827;
}

.show-rate-track {
  display: flex;
  height: 8px;
  border-radius: 999px;
  overflow: hidden;
  background: #f3f4f6;
}

.show-rate-held {
  background: #10b981;
  transition: width 0.3s ease;
}

.show-rate-noshow {
  background: #fca5a5;
  transition: width 0.3s ease;
}

.upcoming {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f5;
}

.upcoming-heading {
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 10px;
}

.upcoming-loading {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}

.upcoming-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px 0;
  color: #9ca3af;
  font-size: 0.85rem;
}

.upcoming-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.upcoming-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  margin: 0 -8px;
  border-radius: 10px;
  transition: background 0.15s ease;
}

.upcoming-item {
  cursor: pointer;
}

.upcoming-item:hover {
  background: #f8fafc;
}

.upcoming-item:focus-visible {
  outline: 2px solid #0ea5e9;
  outline-offset: -2px;
}

.upcoming-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
  background: #f0f9ff;
  color: #0369a1;
}

.upcoming-date--today {
  background: #0ea5e9;
  color: #ffffff;
}

.upcoming-date-month {
  font-size: 0.62rem;
  font-weight: 600;
  text-transform: uppercase;
  line-height: 1;
}

.upcoming-date-day {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.15;
}

.upcoming-info {
  flex: 1;
  min-width: 0;
}

.upcoming-name {
  font-size: 0.88rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: capitalize;
}

.upcoming-meta {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.76rem;
  color: #6b7280;
  min-width: 0;
}

.upcoming-sep {
  color: #d1d5db;
}

.upcoming-company {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.upcoming-badge {
  font-size: 0.7rem;
  font-weight: 600;
  color: #0369a1;
  background: #e0f2fe;
  padding: 3px 8px;
  border-radius: 999px;
  flex-shrink: 0;
}

.upcoming-join {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  color: #0ea5e9;
  flex-shrink: 0;
  transition: background 0.15s ease;
}

.upcoming-join:hover {
  background: #e0f2fe;
}
</style>
